import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { menuTitles } from "../../constant/menuTitle";
import { Box, Stack, Typography, Avatar, Tooltip, IconButton } from "@mui/material";
import { useTicketData } from "../../context/TicketContext";
import { calculateTicketRemainingTime } from "../../utils/timerUtils";
import hrLoginStatusService from "../../services/hrLoginStatus.service";
import itLoginStatusService from "../../services/itLoginStatus.service";
import alarmClock from "../../assets/images/alarm-clock.png";
import clockcheck from "../../assets/images/clock-check.png";
import tableClock from "../../assets/images/tabler_clock-x.png";
import UserProfileDialog from "./UserProfileDialog";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

const Header = ({ sidebarHidden = false, onShowSidebar }) => {
  const [title, setTitle] = useState("");
  const [ticketCounts, setTicketCounts] = useState({
    within3Hours: 0,
    at3Hours: 0,
    without3Hours: 0
  });
  const [isAssignedToday, setIsAssignedToday] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const location = useLocation();
  const { ticketData, publicHolidays } = useTicketData();

  const userData = {
    name:          localStorage.getItem("name") || "",
    userName:      localStorage.getItem("userName") || "",
    email:         localStorage.getItem("email") || "",
    role:          localStorage.getItem("role") || "",
    isOverAllAdmin: localStorage.getItem("isOverAllAdmin") || "false",
  };
  const displayName = userData.name || userData.userName || "User";
  const userInitial = displayName.charAt(0).toUpperCase();

  useEffect(() => {
    const currentPage = location.pathname?.split("/")[1] || "";
    setTitle(menuTitles[currentPage] || "");
  }, [location.pathname]);

  // Check today's assignment status
  useEffect(() => {
    const checkAssignment = async () => {
      const userId = localStorage.getItem("userId");
      const role = localStorage.getItem("role");
      if (role?.toUpperCase() === "HR" && userId) {
        try {
          const res = await hrLoginStatusService.checkHrLoginStatusToday(userId);
          const assignedId = res?.data?.loginRecord?.assignedHrId;
          const responsibleId = res?.data?.loginRecord?.hrId;

          if (responsibleId) {
            // If someone has already claimed responsibility, only they see the badge
            setIsAssignedToday(String(responsibleId) === String(userId));
          } else if (assignedId) {
            // If no one has claimed responsibility yet, show it to the admin's choice
            setIsAssignedToday(String(assignedId) === String(userId));
          } else {
            setIsAssignedToday(false);
          }

        } catch (error) {
          console.error("Header - Error checking assignment:", error);
        }
      } else if (role?.toUpperCase() === "IT SUPPORT" && userId) {
        // Same rule for IT Support: whoever took today, else the admin's pick
        try {
          const res = await itLoginStatusService.checkItLoginStatusToday();
          const record = res?.data?.loginRecord;
          const todayId = record?.userId || record?.assignedUserId;
          setIsAssignedToday(!!todayId && String(todayId) === String(userId));
        } catch (error) {
          console.error("Header - Error checking IT Support assignment:", error);
        }
      }
    };
    checkAssignment();
  }, [location.pathname]);

  // Calculate counts whenever ticketData changes
  useEffect(() => {
    if (location.pathname === '/feedback' && ticketData && ticketData.length > 0) {
      console.log("=== CALCULATING TICKET COUNTS ===");
      console.log("Parent objects:", ticketData.length);

      let within = 0;   // Green badges with 3+ hours remaining
      let at3hrs = 0;   // Green badges with 0-3 hours remaining
      let without = 0;  // Red badges (overdue)
      let totalTickets = 0;

      const THREE_HOURS_MS = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

      // Iterate through parent objects (grouped by assignee)
      ticketData.forEach((parentObj, parentIndex) => {
        const tickets = parentObj.dataInfo || [];

        console.log(`Parent ${parentIndex + 1} (${parentObj.assignedToInfo?.name}): ${tickets.length} tickets`);

        // Iterate through individual tickets
        tickets.forEach((ticket) => {
          totalTickets++;

          // Calculate remaining time using Timer logic
          const remainingMs = calculateTicketRemainingTime(
            ticket.createdAt,
            Number(ticket.expiry) || 2, // completion window in days
            9, // DURATION (9 working hours per day)
            publicHolidays,
            ticket.ticketStatus,
            ticket.tempEndTime || null,
            18, // WORK_END_HOUR
            0,  // minutes
            0   // seconds
          );

          if (totalTickets <= 3) { // Log first 3 tickets for debugging
            console.log(`  Ticket ${totalTickets}:`, {
              ticketId: ticket.ticketId,
              createdAt: ticket.createdAt,
              remainingMs: remainingMs,
              remainingHours: (remainingMs / (1000 * 60 * 60)).toFixed(2) + 'hrs',
              category: remainingMs >= THREE_HOURS_MS ? '🟢 Within' :
                remainingMs >= 0 ? '🟠 Within 3 Hours' :
                  '🔴 Without'
            });
          }

          // Categorize based on remaining time
          if (remainingMs >= THREE_HOURS_MS) {
            // Green badge with 3+ hours remaining
            within++;
          } else if (remainingMs >= 0) {
            // Orange badge with 0-3 hours remaining
            at3hrs++;
          } else {
            // Red badge (overdue)
            without++;
          }
        });
      });

      console.log("=== FINAL COUNTS ===");
      console.log("Total tickets:", totalTickets);
      console.log("🟢 Within (3+ hrs remaining):", within);
      console.log("🟠 Within 3 Hours (0-3 hrs remaining):", at3hrs);
      console.log("🔴 Without (overdue):", without);

      setTicketCounts({
        within3Hours: within,
        at3Hours: at3hrs,
        without3Hours: without
      });
    } else if (location.pathname !== '/feedback') {
      setTicketCounts({
        within3Hours: 0,
        at3Hours: 0,
        without3Hours: 0
      });
    }
  }, [ticketData, publicHolidays, location.pathname]);

  return (
    <Stack
      direction={"row"}
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {sidebarHidden && (
          <Tooltip title="Show sidebar">
            <IconButton
              onClick={onShowSidebar}
              size="small"
              sx={{
                backgroundColor: "#009688",
                color: "#fff",
                width: 30,
                height: 30,
                "&:hover": { backgroundColor: "#00796B" },
              }}
            >
              <MenuOpenIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          </Tooltip>
        )}
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
        {isAssignedToday && (location.pathname === '/' || location.pathname === '/dashboard') && (
          <Box sx={{
            px: 1.5,
            py: 0.6,
            borderRadius: "6px",
            bgcolor: '#e8f5e9',
            color: '#2e7d32',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: '1px solid #c8e6c9',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2e7d32' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '10px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Today's Responsible
            </Typography>
          </Box>
        )}
        {location.pathname === '/feedback' && (
          <Box sx={{ display: "flex", gap: 1.5, mr: 1, alignItems: 'center' }}>
            {/* Within (Green) */}
            <Box sx={{
              px: 2,
              py: 0.75,
              borderRadius: "6px",
              bgcolor: '#E5F9F7',
              color: '#00675D',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid #D5D7E8',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
              <img src={clockcheck} alt="check" style={{ width: 20, height: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '16px', fontFamily: "'Inter', sans-serif" }}>
                Within: {ticketCounts.within3Hours}
              </Typography>
            </Box>

            {/* Within 3 Hours (Orange) */}
            <Box sx={{
              px: 2,
              py: 0.75,
              borderRadius: "6px",
              bgcolor: '#FFF3E0',
              color: '#FF6700',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid #D5D7E8',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
              <img src={alarmClock} alt="alarm" style={{ width: 20, height: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '16px', fontFamily: "'Inter', sans-serif" }}>
                Within 3h: {ticketCounts.at3Hours}
              </Typography>
            </Box>

            {/* Without (Red) */}
            <Box sx={{
              px: 2,
              py: 0.75,
              borderRadius: "6px",
              bgcolor: '#FFEBEE',
              color: '#DB3A3A',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid #D5D7E8',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
              <img src={tableClock} alt="clock-x" style={{ width: 20, height: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '16px', fontFamily: "'Inter', sans-serif" }}>
                Without: {ticketCounts.without3Hours}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Avatar + name + role — click to open profile popover */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={(e) => setProfileAnchor(e.currentTarget)}
          sx={{ cursor: "pointer" }}
        >
          <Avatar
            sx={{
              height: 36, width: 36,
              bgcolor: "#0D9488",
              fontSize: "15px",
              fontWeight: 700,
            }}
          >
            {userInitial}
          </Avatar>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", lineHeight: 1.3, textTransform: "capitalize" }}>
              {displayName}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#64748B", lineHeight: 1.2, textTransform: "capitalize" }}>
              {userData.isOverAllAdmin === "true" ? "OverAll Admin" : userData.role || "Member"}
            </Typography>
          </Box>
        </Stack>

        <UserProfileDialog
          anchorEl={profileAnchor}
          handleClose={() => setProfileAnchor(null)}
          userData={userData}
        />
      </Box>
    </Stack>
  );
};

export default Header;
